import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Field, FieldRenderProps } from 'react-final-form';
import { FieldArrayRenderProps } from 'react-final-form-arrays';
import { Button, FieldError } from '@/ui';

interface CustomVariableItemFinalProps {
  customVariablesItem: string;
  index: number;
  fields: FieldArrayRenderProps<any, HTMLElement>['fields'];
  customVarFlag: string;
}

const Value: React.FC<FieldRenderProps<any, HTMLElement>> = ({ input, meta }) => (
  <>
    <TextField {...input} placeholder='Value' multiline minRows={1} />
    {!input.value && (
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}
      >
        <InfoOutlined sx={{ fontSize: '0.875rem' }} />
        If the value is left empty, the Layout/Container must have a failsafe configured.
      </Typography>
    )}
    <FieldError error={meta.error} touched={meta.touched} />
  </>
);

interface KeyProps extends FieldRenderProps<string, HTMLElement> {
  customVarFlag: string;
}

const Key: React.FC<KeyProps> = ({ input, meta, customVarFlag }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    input.onChange(customVarFlag + e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const currentValue = e.target.value;
    if (!currentValue.startsWith(customVarFlag)) {
      input.onChange(customVarFlag + currentValue);
    }
    // Call onBlur without arguments to notify Final Form the field was blurred
    input.onBlur();
  };

  const displayValue =
    input.value && input.value.startsWith(customVarFlag) ? input.value.slice(customVarFlag.length) : input.value;

  return (
    <TextField
      {...input}
      type='text'
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder='Key'
      error={Boolean(meta.touched && meta.error)}
      helperText={meta.touched && meta.error ? meta.error : undefined}
      slotProps={{ input: { startAdornment: <InputAdornment position='start'>{customVarFlag}</InputAdornment> } }}
    />
  );
};

export default function CustomVariableItemFinal({
  customVariablesItem,
  index,
  fields,
  customVarFlag,
}: CustomVariableItemFinalProps) {
  return (
    <Card sx={{ p: 1, mb: 1 }}>
      <CardContent>
        <div style={{ marginBottom: '.5rem' }}>
          <Button
            color='danger'
            type='button'
            title='Remove Parent Attribute'
            aria-label='Remove Parent Attribute'
            onClick={() => fields.remove(index)}
            size='sm'
          >
            <Close fontSize='small' />
          </Button>
        </div>
        <div style={{ marginBottom: '.5rem' }}>
          <Field name={`${customVariablesItem}.key`} type='text' component={Key} customVarFlag={customVarFlag} />
        </div>
        <div>
          <Field name={`${customVariablesItem}.value`} type='text' component={Value} />
        </div>
      </CardContent>
    </Card>
  );
}
